import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBlockedUserIds } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB "Recent Comments" rail box.
 *
 * Data source: `hub_post_comments` — approved, non-deleted comments across
 * ALL Hub posts, newest first. Joins `hub_settings` (a hub_ table) for the
 * commenter's Hub display name and avatar.
 *
 * ISOLATION: hub_post_comments / hub_settings only. No DMs, no core chat,
 * no messaging tables are read here.
 */

export interface HubRecentComment {
  id: string;
  post_id: string;
  author_id: string;
  body: string;
  created_at: string;
  author: {
    display_name: string;
    avatar_path: string | null;
  } | null;
}

export function useHubRecentComments(limit = 5) {
  const { blockedIds } = useBlockedUserIds();
  return useQuery({
    queryKey: ["hub-recent-comments", limit, blockedIds.join(",")],
    staleTime: 60_000,
    queryFn: async (): Promise<HubRecentComment[]> => {
      let q = supabase
        .from("hub_post_comments")
        .select(
          "id, post_id, author_id, body, created_at, author:hub_settings(display_name, avatar_path)"
        )
        .eq("is_deleted", false)
        .eq("moderation_status", "approved")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (blockedIds.length) q = q.not("author_id", "in", `(${blockedIds.join(",")})`);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as HubRecentComment[];
    },
  });
}

/** First non-empty line of a comment body, collapsed to one short line. */
export function hubCommentSnippet(body: string, max = 55): string {
  const line = (body ?? "").split(/\r?\n/).find((l) => l.trim().length > 0) ?? "";
  const clean = line.trim().replace(/\s+/g, " ");
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

export default useHubRecentComments;
